import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import VideoIcon from '@atlaskit/icon/core/video';

import { getHashedLoomVideoIdFromUrl } from '@atlassian/analytics-data-privacy/get-hashed-loom-video-id-from-url';
import { Ati } from '@atlassian/ari';

import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant.ts';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils/entry-points/useLoomRecorderEntryPoint.ts';
import { LoomCrossJoinPopupCard } from '@confluence/loom-utils/entry-points/LoomCrossJoinPopupCard.ts';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

import { buildCreationLink } from '../../CreateContentRouteComponent/index.ts';
import { ItemTitleWithLozenge } from '../ItemTitle.tsx';

const ANALYTICS_EVENT_SOURCE = 'globalCreateContentPopover';

const CROSS_JOIN = 'CROSS_JOIN';

const messages = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.loom.title',
		defaultMessage: 'Loom video',
		description:
			'title of the global create menu item indicating that this option will trigger a Loom video recording flow',
	},
	crossJoinPopupTitle: {
		id: 'create-blank-fabric-page.universal-create.loom.crossJoinPopupTitle',
		defaultMessage: 'Share your work with a video',
		description: 'Title of a popup card advertising an opportunity to try Loom',
	},
	crossJoinPopupDescription: {
		id: 'create-blank-fabric-page.universal-create.loom.crossJoinPopupDescription',
		defaultMessage:
			'Save time and engage your team with a video walkthrough, demo, update, and more.',
		description: 'Description inside a popup card advertising an opportunity to try Loom',
	},
	crossJoinCTA: {
		id: 'create-blank-fabric-page.universal-create.loom.crossJoinCTA',
		defaultMessage: 'Try now',
		description: 'text on button that launches a cross-join flow for Loom',
	},
	crossJoinLozenge: {
		id: 'create-blank-fabric-page.universal-create.loom.cross-join-lozenge',
		defaultMessage: 'Join',
		description:
			'label advertising the ability to join teammates in using the the Loom video recording feature from the global and contextual menus',
	},
});

export function useLoomCrossJoinItem(
	flags: FlagsStateContainer,
	loading?: boolean,
	source?: string,
): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();

	const PopupWrapper = ({ children }) => (
		<LoomCrossJoinPopupCard
			description={formatMessage(messages.crossJoinPopupDescription)}
			entryPointId="loom-confluence-create-menu"
			sourceComponent={source}
			shouldRenderToParent={false} // need to overwrite the default value
			title={formatMessage(messages.crossJoinPopupTitle)}
			sourceActionSubjectId="createLoomPopoverItem"
			triggerLink={(_, triggerProps) =>
				children({
					triggerProps,
				})
			}
			flags={flags}
		/>
	);

	return {
		ati: Ati.parse(`ati:cloud:loom:video`),
		isLoading: loading,
		target: '_blank',
		testId: 'create-menu-link-item',
		children: (
			<ItemTitleWithLozenge lozenge={formatMessage(messages.crossJoinLozenge)}>
				{formatMessage(messages.title)}
			</ItemTitleWithLozenge>
		),
		Wrapper: PopupWrapper,
		elemBefore: VideoIcon,
	};
}

export const useRecordLoomItem = ({
	parentId,
	source,
	spaceKey,
	onClick,
	flags,
}: UseMenuItemProps & {
	flags: FlagsStateContainer;
}): UseMenuItemReturnProps => {
	const { formatMessage } = useIntl();
	const spaceId = useSpaceId();
	const { push } = useRouteActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { openLoomRecorder, isLoomRecorderInitialized } = useLoomRecorderEntryPoint({
		entryPointLocation: ANALYTICS_EVENT_SOURCE,
		flags,
	});

	const { enforceStorageLimit } = useHardStorageEnforcement({
		source: 'record-loom-popover-item',
	});

	const handleClick = useCallback(() => {
		onClick('createLoomPopoverItem');

		void openLoomRecorder({
			onInsert: (videoUrl: string) => {
				const createUrl = buildCreationLink({
					contentType: 'smartLinkEmbed',
					parentPageId: parentId,
					withFallback: true,
					smartLinkCreateSource: 'loomRecordSDK',
					smartLinkEmbedUrl: videoUrl,
					source,
					spaceKey,
				});

				push(createUrl);

				void getHashedLoomVideoIdFromUrl(videoUrl).then((hashedVideoId) => {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'insertVideo',
							actionSubject: 'loom',
							source,
							objectType: 'embed',
							containerType: 'space',
							containerId: spaceId,
							attributes: {
								location: 'contentTreeItem',
								video_id: hashedVideoId,
							},
						},
					}).fire();
				});
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Insert video',
			location: 'topNav',
		});
	}, [onClick, openLoomRecorder, parentId, source, spaceKey, push, createAnalyticsEvent, spaceId]);

	return {
		ati: Ati.parse(`ati:cloud:loom:video`),
		children: formatMessage(messages.title),
		isLoading: !isLoomRecorderInitialized,
		elemBefore: VideoIcon,
		testId: 'create-menu-button-item',
		onClick: enforceStorageLimit(handleClick),
	};
};

export function useLoomItem(
	props: UseMenuItemProps & {
		flags: FlagsStateContainer;
	},
): UseMenuItemReturnProps {
	const { entryPointVariant, loading } = useLoomEntryPointVariant();
	const recordItem = useRecordLoomItem(props);
	const crossJoinItem = useLoomCrossJoinItem(props.flags, loading, props.source);
	return entryPointVariant === CROSS_JOIN ? crossJoinItem : recordItem;
}
